/**
 * Optional manifest.json served at /replays/manifest.json. When present, the
 * library auto-loads every listed .jsonl so the UI works on a static host
 * (Cloudflare Pages / Netlify / a Vite preview) without drag-and-drop.
 */

import type { Replay } from "./types.js";
import { parseReplayText } from "./replayLoader.js";

export interface ManifestEntry {
  path: string;                  // relative to base, e.g. "game_1.jsonl"
  game_id?: string;
  seed?: number;
  status?: string;
  winner?: string | null;
  flags?: string[];
}

export interface Manifest {
  schema: "replays/manifest/v1";
  generated_at: string;
  base: string;                  // e.g. "/replays"
  entries: ManifestEntry[];
}

export interface LoadManifestOptions {
  /** URL of manifest.json — defaults to /replays/manifest.json. */
  url?: string;
  /** Max concurrent JSONL fetches. */
  concurrency?: number;
  /** AbortSignal so tests / navigations can cancel. */
  signal?: AbortSignal;
}

export async function loadManifest(
  opts: LoadManifestOptions = {},
): Promise<{ replays: Replay[]; errors: string[] } | null> {
  const url = opts.url ?? "/replays/manifest.json";
  const fetchOpts: RequestInit = {};
  if (opts.signal) fetchOpts.signal = opts.signal;
  let manifest: Manifest;
  try {
    const res = await fetch(url, fetchOpts);
    if (!res.ok) return null; // no manifest present is a normal case
    manifest = (await res.json()) as Manifest;
  } catch (err) {
    if ((err as { name?: string }).name === "AbortError") return null;
    return null;
  }
  if (manifest.schema !== "replays/manifest/v1") {
    return {
      replays: [],
      errors: [`unsupported manifest schema: ${(manifest as { schema?: string }).schema}`],
    };
  }
  const base = manifest.base.replace(/\/$/, "");
  const concurrency = Math.max(1, opts.concurrency ?? 4);
  const errors: string[] = [];
  const replays: Replay[] = [];
  const queue = [...manifest.entries];

  async function worker(): Promise<void> {
    while (queue.length > 0) {
      if (opts.signal?.aborted) return;
      const entry = queue.shift();
      if (!entry) return;
      const path = entry.path.startsWith("/")
        ? entry.path
        : `${base}/${entry.path}`;
      try {
        const res = await fetch(path, fetchOpts);
        if (!res.ok) {
          errors.push(`${entry.path}: HTTP ${res.status}`);
          continue;
        }
        const text = await res.text();
        replays.push(parseReplayText(text, entry.path));
      } catch (err) {
        if (isAbortError(err) || opts.signal?.aborted) throw err;
        errors.push(`${entry.path}: ${String(err)}`);
      }
    }
  }

  try {
    await Promise.all(
      Array.from({ length: Math.min(concurrency, queue.length) }, worker),
    );
  } catch (err) {
    if (isAbortError(err) || opts.signal?.aborted) return null;
    throw err;
  }
  return { replays, errors };
}

function isAbortError(err: unknown): boolean {
  return (err as { name?: string }).name === "AbortError";
}
